// sessionExpiry.ts — #2189.
//
// api.ts's handleSessionExpired() used to unconditionally clear the session on a failed
// token refresh. That raced any page that signs the visitor in from a URL token (e.g.
// ResumePage's /resume exchange): a STALE access+refresh token pair already in
// localStorage makes the app's boot-time getMe() -> refresh chain fail asynchronously: if
// that failure resolves AFTER the page's own exchange already set a NEWER, valid session,
// the unconditional clear wiped the fresh tokens moments after they were applied.
//
// This is the pure decision the fix turns on: given the specific refresh token that just
// failed, is it still the one in effect? Pulled out standalone (not left inline in api.ts,
// whose `import.meta.env` usage can't load under ts-jest's CommonJS transform — see
// utils/doneForYouLink.ts) so it's directly unit-testable.

/**
 * Should a failed token refresh actually clear the session? `failedToken` is the refresh
 * token that was being attempted when it failed; `currentToken` is whatever refresh token
 * is in effect right now (read at the moment of the decision, not when the refresh
 * started). True for an unconditional expiry (`failedToken` null — no specific token to
 * compare, the original behavior) or when the failed token is STILL the current one
 * (a genuine, unreplaced expiry). False when they differ: something else — a newer sign-in
 * — already replaced the session that failed, so clearing now would wipe a valid session
 * instead of an expired one.
 */
export function shouldClearExpiredSession(failedToken: string | null, currentToken: string | null): boolean {
  return failedToken === null || failedToken === currentToken;
}
